// AddPlanModal.tsx — Co Marca
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Notebook, CheckCircle2 } from 'lucide-react';
import { createPlan, Plan } from '../../services/planService';

interface AddPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (newPlan: Plan) => void;
}

const C = { primary:'#c1348b', primaryDark:'#8f1f64', secondary:'#61257b', secondaryDark:'#004d7a', secondaryLight:'#8b3fa8', accentDark:'#ea8406' };

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.apm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes apmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes apmSlideUp{from{opacity:0;transform:translateY(20px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.apm-overlay{animation:apmFadeIn .22s ease both;}
.apm-dialog{animation:apmSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.apm-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;font-family:'DM Sans',sans-serif;resize:vertical;}
.apm-input:focus{border-color:${C.secondary};box-shadow:0 0 0 3px rgba(97, 37, 123,.1);background:#fff;}
.apm-btn-primary{display:inline-flex;align-items:center;gap:6px;padding:10px 22px;border-radius:11px;border:none;background:linear-gradient(135deg,${C.primary},${C.primaryDark});color:#fff;font-weight:800;font-size:.88rem;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s;}
.apm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.34);}
.apm-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.apm-btn-ghost{display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border-radius:11px;border:1.5px solid #e2e8f0;background:#f8fafc;color:#374151;font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;}
.apm-btn-ghost:hover{background:#f1f5f9;}
`;

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v:boolean)=>void }) => (
    <div onClick={()=>onChange(!checked)} style={{width:'42px',height:'22px',borderRadius:'100px',background:checked?C.secondary:'#d1d5db',position:'relative',cursor:'pointer',transition:'background .25s',flexShrink:0}}>
        <div style={{position:'absolute',top:'3px',left:checked?'23px':'3px',width:'16px',height:'16px',borderRadius:'50%',background:'#fff',boxShadow:'0 1px 3px rgba(0,0,0,.2)',transition:'left .25s'}}/>
    </div>
);

const AddPlanModal: React.FC<AddPlanModalProps> = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState({ NombrePlan:'', Comentarios:'', estado:true });
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');

    useEffect(()=>{ if(isOpen){ setFormData({NombrePlan:'',Comentarios:'',estado:true}); setError(''); } },[isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); setLoading(true); setError('');
        try { const p=await createPlan(formData); onCreated(p); onClose(); }
        catch { setError('Error al crear el plan. Verifica los datos.'); }
        finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="apm-modal apm-overlay" style={{position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',background:'rgba(15,23,42,.55)',backdropFilter:'blur(6px)'}}>
                <div className="apm-dialog" onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px',width:'100%',maxWidth:'500px',boxShadow:'0 32px 80px rgba(0,0,0,.2)',border:'1.5px solid #f1f5f9',overflow:'hidden'}}>

                    {/* Barra top roja */}
                    <div style={{height:'4px',background:`linear-gradient(90deg,${C.primary},${C.primaryDark},${C.accentDark},${C.primary}80)`}}/>

                    {/* Header */}
                    <div style={{padding:'20px 24px',borderBottom:'1.5px solid #f1f5f9',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                            <div style={{width:'38px',height:'38px',borderRadius:'12px',background:`linear-gradient(135deg,${C.primary},${C.primaryDark})`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 4px 12px ${C.primary}30`}}>
                                <Plus size={18} color="#fff"/>
                            </div>
                            <div>
                                <h3 style={{fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Nuevo Plan</h3>
                                <p style={{fontSize:'.72rem',color:'#94a3b8',marginTop:'1px'}}>Completa los campos para crear el plan</p>
                            </div>
                        </div>
                        <button onClick={onClose} style={{width:'30px',height:'30px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{padding:'20px 24px 24px',display:'flex',flexDirection:'column',gap:'16px'}}>
                        {error&&(
                            <div style={{display:'flex',alignItems:'center',gap:'7px',padding:'9px 12px',borderRadius:'10px',background:`${C.primary}08`,border:`1.5px solid ${C.primary}22`}}>
                                <X size={12} color={C.primary}/>
                                <span style={{fontSize:'.82rem',color:C.primaryDark,fontWeight:'600'}}>{error}</span>
                            </div>
                        )}

                        {/* Nombre */}
                        <div>
                            <label style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'7px'}}>
                                <Notebook size={10} color={C.secondary}/> Nombre del Plan *
                            </label>
                            <input type="text" required value={formData.NombrePlan} onChange={e=>setFormData({...formData,NombrePlan:e.target.value})} placeholder="Ej: Plan Premium" className="apm-input"/>
                        </div>

                        {/* Comentarios */}
                        <div>
                            <label style={{display:'block',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'7px'}}>Comentarios</label>
                            <textarea value={formData.Comentarios} onChange={e=>setFormData({...formData,Comentarios:e.target.value})} rows={3} placeholder="Descripción o notas del plan..." className="apm-input"/>
                        </div>

                        {/* Estado */}
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 14px',background:'#f8fafc',borderRadius:'12px',border:'1.5px solid #f1f5f9'}}>
                            <div>
                                <p style={{fontSize:'.85rem',fontWeight:'700',color:'#374151'}}>Estado del Plan</p>
                                <p style={{fontSize:'.72rem',color:'#94a3b8',marginTop:'2px'}}>{formData.estado?'Visible y activo en el sistema':'Oculto e inactivo'}</p>
                            </div>
                            <Toggle checked={formData.estado} onChange={v=>setFormData({...formData,estado:v})}/>
                        </div>

                        {/* Botones */}
                        <div style={{display:'flex',justifyContent:'flex-end',gap:'8px',paddingTop:'4px',borderTop:'1.5px solid #f1f5f9',marginTop:'4px'}}>
                            <button type="button" onClick={onClose} className="apm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="apm-btn-primary">
                                {loading
                                    ?<><span style={{width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block'}}/> Guardando...</>
                                    :<><CheckCircle2 size={14}/> Crear Plan</>
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AddPlanModal;